import { useState, useRef } from 'react';
import { insightArticles, insightCategories } from '@/mocks/insights';
import InsightCard from './InsightCard';

export default function InsightsList() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const nonFeatured = insightArticles.filter((a) => !a.featured);

  // 1. Category filter
  const byCategory =
    activeCategory === 'all'
      ? nonFeatured
      : nonFeatured.filter((a) => a.category === activeCategory);

  // 2. Search filter (title + excerpt + tags)
  const q = searchQuery.trim().toLowerCase();
  const filtered = q
    ? byCategory.filter(
        (a) =>
          a.title.toLowerCase().includes(q) ||
          a.excerpt.toLowerCase().includes(q) ||
          a.tags.some((t) => t.toLowerCase().includes(q)),
      )
    : byCategory;

  const recent = insightArticles
    .slice()
    .sort((a, b) => b.publishDate.localeCompare(a.publishDate))
    .slice(0, 4);

  const allTags = Array.from(new Set(insightArticles.flatMap((a) => a.tags)));

  const handleTagClick = (tag: string) => {
    setSearchQuery(tag);
    inputRef.current?.focus();
  };

  const clearSearch = () => {
    setSearchQuery('');
    inputRef.current?.focus();
  };

  return (
    <section className="py-16 md:py-24 bg-[#fafaf8]">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">

          {/* Main content */}
          <div className="flex-1 min-w-0">

            {/* Search bar */}
            <div className="relative mb-6">
              <div className="flex items-center gap-3 px-4 py-3 bg-white border border-[#e2ddd8] rounded-xl focus-within:border-[#b8956a] transition-colors">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-search-line text-[#b0a898] text-base"></i>
                </div>
                <input
                  ref={inputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="搜尋文章標題、關鍵字或標籤…"
                  className="flex-1 bg-transparent text-sm text-[#1a1a1a] placeholder-[#c0bbb5] focus:outline-none"
                />
                {searchQuery && (
                  <button
                    onClick={clearSearch}
                    className="w-6 h-6 flex items-center justify-center rounded-full bg-[#ede9e4] hover:bg-[#e2ddd8] transition-colors cursor-pointer flex-shrink-0"
                    aria-label="清除搜尋"
                  >
                    <i className="ri-close-line text-[#8a8a8a] text-xs"></i>
                  </button>
                )}
              </div>

              {/* Search result hint */}
              {q && (
                <div className="flex items-center gap-2 mt-2.5 px-1">
                  <i className="ri-search-line text-[#b8956a] text-xs"></i>
                  <p className="text-xs text-[#8a8a8a]">
                    「<strong className="text-[#1a1a1a]">{searchQuery}</strong>」共找到{' '}
                    <strong className="text-[#b8956a]">{filtered.length}</strong> 篇文章
                  </p>
                </div>
              )}
            </div>

            {/* Category filter */}
            <div className="flex flex-wrap gap-2 mb-10">
              {insightCategories.map((cat) => {
                const base = cat.key === 'all' ? nonFeatured : nonFeatured.filter((a) => a.category === cat.key);
                const count = q
                  ? base.filter(
                      (a) =>
                        a.title.toLowerCase().includes(q) ||
                        a.excerpt.toLowerCase().includes(q) ||
                        a.tags.some((t) => t.toLowerCase().includes(q)),
                    ).length
                  : base.length;
                const isActive = activeCategory === cat.key;
                return (
                  <button
                    key={cat.key}
                    onClick={() => setActiveCategory(cat.key)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                      isActive
                        ? 'bg-[#1a1a1a] text-white'
                        : 'bg-white text-[#5a5a5a] border border-[#e2ddd8] hover:border-[#b8956a] hover:text-[#b8956a]'
                    }`}
                  >
                    {cat.label}
                    <span className={`ml-1.5 text-xs ${isActive ? 'text-white/60' : 'text-[#b0a898]'}`}>
                      ({count})
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Articles grid */}
            {filtered.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {filtered.map((article) => (
                  <InsightCard key={article.id} article={article} />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-[#b0a898]">
                {q ? (
                  <>
                    <div className="w-14 h-14 flex items-center justify-center bg-[#f0ede8] rounded-full mb-5">
                      <i className="ri-search-line text-2xl text-[#c8a87a]"></i>
                    </div>
                    <p className="text-[#5a5a5a] font-medium mb-2">找不到相關文章</p>
                    <p className="text-sm text-[#b0a898] mb-5">
                      試試其他關鍵字，或瀏覽下方熱門標籤
                    </p>
                    <button
                      onClick={clearSearch}
                      className="inline-flex items-center gap-2 px-5 py-2.5 border border-[#e2ddd8] text-[#5a5a5a] text-sm rounded-full hover:border-[#b8956a] hover:text-[#b8956a] transition-colors cursor-pointer"
                    >
                      <i className="ri-close-line text-xs"></i>
                      清除搜尋
                    </button>
                  </>
                ) : (
                  <>
                    <i className="ri-file-search-line text-4xl mb-4"></i>
                    <p className="text-sm">此分類暫無文章</p>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <aside className="lg:w-72 flex-shrink-0">
            <div className="lg:sticky lg:top-28 flex flex-col gap-8">

              {/* Recent articles */}
              <div>
                <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#b8956a] mb-5">最新文章</p>
                <div className="flex flex-col divide-y divide-[#ede9e4]">
                  {recent.map((article, i) => (
                    <div key={article.id} className={i === 0 ? 'pb-5' : 'py-5'}>
                      <InsightCard article={article} variant="horizontal" />
                    </div>
                  ))}
                </div>
              </div>

              {/* Topics — clickable to search */}
              <div>
                <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#b8956a] mb-5">熱門標籤</p>
                <div className="flex flex-wrap gap-2">
                  {allTags.map((tag) => {
                    const isActive = searchQuery === tag;
                    return (
                      <button
                        key={tag}
                        onClick={() => handleTagClick(tag)}
                        className={`text-xs px-3 py-1.5 rounded-full cursor-pointer transition-colors whitespace-nowrap ${
                          isActive
                            ? 'bg-[#b8956a] text-white border border-[#b8956a]'
                            : 'bg-white border border-[#e2ddd8] text-[#5a5a5a] hover:border-[#b8956a] hover:text-[#b8956a]'
                        }`}
                      >
                        #{tag}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* CTA card */}
              <div className="p-6 rounded-xl bg-[#1a1a1a] text-white">
                <p className="text-[#b8956a] text-xs font-medium tracking-widest uppercase mb-3">免費諮詢</p>
                <p className="font-semibold leading-[1.5] mb-4" style={{ fontSize: '1.0625rem' }}>
                  有空間規劃的問題？讓我們直接聊
                </p>
                <p className="text-white/50 text-sm leading-relaxed mb-5">
                  初次諮詢完全免費，無壓力、無推銷，只有對你真正有價值的建議。
                </p>
                <a
                  href="/contact"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#b8956a] text-white text-sm font-medium rounded-full hover:bg-[#a07d58] transition-colors cursor-pointer whitespace-nowrap"
                >
                  預約諮詢
                  <i className="ri-arrow-right-line text-xs"></i>
                </a>
              </div>

            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}
